import qs from "query-string";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NavigateFunction } from "react-router";

type DefaultParams = Record<string, string | undefined>;
type ParamValues<T extends DefaultParams> = { [K in keyof T]: string | undefined };
type ParamUpdates<T extends DefaultParams> = Partial<{ [K in keyof T]: string | undefined }>;

type SetParam<T extends DefaultParams> = {
  (key: keyof T, value: string | undefined): void;
  (updates: ParamUpdates<T>): void;
};

const getSearch = () => window.location.search;

export const useUrlParams = <T extends DefaultParams>(defaults: T): [ParamValues<T>, SetParam<T>] => {
  const [search, setSearch] = useState(getSearch);

  useEffect(() => {
    const onLocationChange = () => setSearch(getSearch());
    window.addEventListener("popstate", onLocationChange);
    return () => window.removeEventListener("popstate", onLocationChange);
  }, []);

  const params = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    const result: Record<string, string | undefined> = {};
    for (const key of Object.keys(defaults)) {
      const raw = urlParams.get(key);
      result[key] = raw !== null ? raw : defaults[key];
    }
    return result as ParamValues<T>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const setParam = useCallback((keyOrUpdates: keyof T | ParamUpdates<T>, value?: string) => {
    const next = new URLSearchParams(window.location.search);
    if (typeof keyOrUpdates === "object") {
      for (const [k, v] of Object.entries(keyOrUpdates)) {
        if (v === undefined) {
          next.delete(k);
        } else {
          next.set(k, v);
        }
      }
    } else {
      if (value === undefined) {
        next.delete(keyOrUpdates as string);
      } else {
        next.set(keyOrUpdates as string, value);
      }
    }
    const nextSearch = next.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
    window.history.replaceState(window.history.state, "", nextUrl);
    setSearch(`?${nextSearch}`);
  }, []) as SetParam<T>;

  return [params, setParam];
};

export const useRouteParams = (search: string): any => {
  let params = {};

  if (search) {
    params = qs.parse(search);
  }

  return params;
};

export const createRouteSearch = (params: object): string => {
  let search = "?";

  let i = 0;
  for (const key in params) {
    const val = params[key];
    if (val) {
      search += `${i !== 0 ? "&" : ""}${key}=${encodeURI(val)}`;
      i++;
    }
  }

  return search;
};

export const updateRouteParams = (navigate: NavigateFunction, pathname: string, params: object, state?: any) => {
  const search = createRouteSearch(params);
  navigate(pathname + search, {
    state: state,
  });
};

export const urlReplace = (str: string, data: object): string => {
  if (str) {
    const keys = Object.keys(data);
    keys.forEach((key) => {
      str = str.replace(`:${key}`, data[key]);
    });
    return str;
  } else {
    return "";
  }
};
